import React, { useState, useEffect, useRef } from 'react';
import { Search, BellRing, Menu, LogOut, ChevronDown, User, Home } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../../../api/axios';
import styles from './Topbar.module.css';

const Topbar = ({ title, onMenuClick, searchQuery = '', setSearchQuery = () => {} }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState({ fullName: 'User', role: 'Resident' });
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const fetchCount = async () => {
      try {
        const { data } = await api.get('/users/notifications');
        const count = data.filter(n => !n.read).length;
        setUnreadCount(count);
      } catch (err) {
        console.error('Failed to fetch notifications for Topbar', err);
      }
    };
    
    // Check if token exists before fetching to avoid 401s on login screen
    if (localStorage.getItem('token')) {
      fetchCount();
      window.addEventListener('notificationUpdated', fetchCount);
    }
    
    return () => {
      window.removeEventListener('notificationUpdated', fetchCount);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleProfileUpdate = () => {
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      if (userInfo && userInfo.fullName) {
        setUser(userInfo);
      }
    };
    
    handleProfileUpdate();
    window.addEventListener('profileUpdated', handleProfileUpdate);
    return () => window.removeEventListener('profileUpdated', handleProfileUpdate);
  }, []);

  const initials = user.fullName 
    ? user.fullName.split(" ").map((n) => n[0]).join("").substring(0, 2).toUpperCase() 
    : "U";
    
  // Determine role path for notification link
  const rolePath = user.role ? `/${user.role}` : '/resident';

  // Determine default dashboard title based on base path
  let displayTitle = title;
  if (!displayTitle) {
    if (location.pathname.startsWith('/admin')) {
      displayTitle = "Admin Dashboard";
    } else if (location.pathname.startsWith('/security')) {
      displayTitle = "Security Dashboard";
    } else if (location.pathname.startsWith('/superadmin')) {
      displayTitle = "Super Admin Dashboard";
    } else {
      displayTitle = "Resident Dashboard";
    }
  }

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userInfo');
    localStorage.removeItem('user');
    navigate('/login');
  };

  return (
    <header className={styles.header}>
      <div className={styles.headerLeft}>
        <Menu size={22} className={styles.menuIcon} onClick={onMenuClick} style={{ cursor: 'pointer' }} />
        <h1>{displayTitle}</h1>
      </div>

      <div className={styles.headerRight}>
        {/* Search */}
        <div className={styles.searchBox}>
          <Search size={16} />
          <input 
            type="text" 
            placeholder="Search on this page..." 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {/* Notification */}
        {user.role !== 'admin' && (
          <button
          className={styles.notificationButton}
          aria-label="Notifications"
          onClick={async () => {
            if (unreadCount > 0) {
              try {
                await api.put('/users/notifications/read');
                setUnreadCount(0);
                window.dispatchEvent(new Event('notificationUpdated'));
              } catch (e) {
                console.error(e);
              }
            }
            navigate(`${rolePath}/notifications`);
          }}
        >
          <BellRing size={19} />
          {unreadCount > 0 && <span className={styles.notificationBadge}>{unreadCount}</span>}
        </button>
        )}

        {/* User */}
        <div className={styles.headerUser} ref={dropdownRef}>
          <div 
            className={styles.userProfileBtn} 
            onClick={() => setShowDropdown(!showDropdown)}
          >
            <div className={styles.headerAvatar}>
              {user.profileImage && user.profileImage.startsWith('/uploads') ? (
                <img src={`${import.meta.env.VITE_API_URL.replace(/\/api\/?$/, '')}${user.profileImage}`} alt="Profile" className={styles.avatarImg} />
              ) : user.profileImage ? (
                <img src={user.profileImage} alt="Profile" className={styles.avatarImg} />
              ) : (
                initials
              )}
            </div>
            <div className={styles.headerUserInfo}>
              <span className={styles.headerUserName}>{user.fullName}</span>
              <span className={styles.headerUserRole} style={{ textTransform: 'capitalize' }}>{user.role || 'Resident'}</span>
            </div>
            <ChevronDown size={16} className={styles.dropdownIcon} />
          </div>

          {/* Dropdown Menu */}
          {showDropdown && (
            <div className={styles.userDropdown}>
              <div className={styles.dropdownHeader}>
                <p className={styles.dropdownName}>{user.fullName}</p>
                <p className={styles.dropdownEmail}>{user.email || ''}</p>
              </div>
              <div className={styles.dropdownBody}>
                <button 
                  className={styles.dropdownItem}
                  onClick={() => {
                    setShowDropdown(false);
                    navigate('/');
                  }}
                >
                  <Home size={16} />
                  <span>Website Home</span>
                </button>
                <button 
                  className={styles.dropdownItem}
                  onClick={() => {
                    setShowDropdown(false);
                    navigate(`${rolePath}/profile`);
                  }}
                >
                  <User size={16} />
                  <span>My Profile</span>
                </button>
                <div className={styles.dropdownDivider}></div>
                <button 
                  className={`${styles.dropdownItem} ${styles.logoutBtn}`}
                  onClick={handleLogout}
                >
                  <LogOut size={16} />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Topbar;
